import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/i18n/LanguageContext";
import logoWhiteOutline from "@/assets/logo-white-outline.webp";

const Footer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  // Ensure video plays on mobile (some browsers block autoplay until interaction or require explicit play)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    const onVisible = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener("visibilitychange", onVisible);
    const onTouch = () => tryPlay();
    document.addEventListener("touchstart", onTouch, { once: true, passive: true });
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("touchstart", onTouch);
    };
  }, []);


  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const footerLinks = {
    [lang === "ar" ? "المتجر" : "Shop"]: [
      { label: lang === "ar" ? "جميع المنتجات" : "All Products", to: "/shop" },
      { label: lang === "ar" ? "الأكثر مبيعاً" : "Best Sellers", action: () => scrollToSection("top-products") },
      { label: lang === "ar" ? "وصل حديثاً" : "New Arrivals", to: "/shop" },
      { label: lang === "ar" ? "حزم" : "Bundles", to: "/shop" },
    ],
    [lang === "ar" ? "الشركة" : "Company"]: [
      { label: lang === "ar" ? "من نحن" : "About Us", action: () => scrollToSection("about") },
      { label: lang === "ar" ? "قصتنا" : "Our Story", to: "/our-story" },
      { label: lang === "ar" ? "وظائف" : "Careers", to: "/careers" },
      { label: lang === "ar" ? "تواصل معنا" : "Contact", action: () => scrollToSection("contact") },
    ],
    [lang === "ar" ? "تابعنا" : "Social"]: [
      { label: "Instagram", to: "https://www.instagram.com/dr.dee.beauty", external: true },
      { label: "TikTok", to: "https://www.tiktok.com/@..dr.dee", external: true },
      { label: "Facebook", to: "https://www.facebook.com/Sorayapalestine/?ref=PROFILE_EDIT_xav_ig_profile_page_web", external: true },
    ],
    [lang === "ar" ? "قانوني" : "Legal"]: [
      { label: lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy", to: "/privacy" },
      { label: lang === "ar" ? "شروط الخدمة" : "Terms of Service", to: "/terms" },
      { label: lang === "ar" ? "سياسة ملفات تعريف الارتباط" : "Cookie Policy", to: "/cookies" },
    ],
  };

  return (
    <footer
      ref={containerRef}
      className="relative min-h-[560px] sm:min-h-[400px] overflow-hidden"
      style={{ background: "#0a0a0a" }}
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        // @ts-ignore — iOS-specific attribute
        webkit-playsinline="true"
        disableRemotePlayback
        preload="auto"
        aria-hidden="true"
        className={isMobile ? "absolute inset-0 w-full h-full object-cover" : "w-full block"}
        poster=""
      >
        <source src="/footer-animation.webm" type="video/webm" />
      </video>


      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-0">
        <div className="mb-5 sm:mb-6 flex items-center justify-center">
          <img
            src={logoWhiteOutline}
            alt="Dr. Dee"
            className="h-[clamp(2.5rem,8vw,7rem)] w-auto select-none opacity-30 sm:opacity-50"
            draggable={false}
          />
        </div>

        <div className="mx-auto w-full max-w-4xl">
          <div className="h-px w-full bg-white/20 mb-5 sm:mb-6" />

          <div className="grid w-full grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4 sm:gap-8">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h3
                  className="mb-2 sm:mb-3 text-xs sm:text-sm font-bold text-white"
                  style={{ fontFamily: lang === "ar" ? fontFamily : "'Poppins', sans-serif" }}
                >
                  {category}
                </h3>
                <ul className="flex flex-col gap-1.5 sm:gap-2">
                  {links.map((link: any) => (
                    <li key={link.label}>
                      {link.action ? (
                        <button
                          onClick={link.action}
                          className="text-xs sm:text-sm text-white/80 transition-all duration-200 hover:text-white hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] sm:text-white sm:hover:text-[#E88CA4] sm:hover:drop-shadow-[0_0_8px_rgba(232,140,164,0.6)]"
                          style={{ fontFamily }}
                        >
                          {link.label}
                        </button>
                      ) : link.external ? (
                        <a
                          href={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs sm:text-sm text-white/80 transition-all duration-200 hover:text-white hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] sm:text-white sm:hover:text-[#E88CA4] sm:hover:drop-shadow-[0_0_8px_rgba(232,140,164,0.6)]"
                          style={{ fontFamily }}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={link.to}
                          className="text-xs sm:text-sm text-white/80 transition-all duration-200 hover:text-white hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] sm:text-white sm:hover:text-[#E88CA4] sm:hover:drop-shadow-[0_0_8px_rgba(232,140,164,0.6)]"
                          style={{ fontFamily }}
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-5 sm:mt-6 flex items-center justify-center gap-3 sm:gap-4 mb-5 sm:mb-6">
            <a
              href="https://www.instagram.com/dr.dee.beauty"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex items-center justify-center w-9 h-9 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/60 hover:bg-white/10 transition-all duration-300"
            >
              <Instagram size={16} />
            </a>
            <a
              href="https://www.facebook.com/Sorayapalestine/?ref=PROFILE_EDIT_xav_ig_profile_page_web"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="flex items-center justify-center w-9 h-9 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/60 hover:bg-white/10 transition-all duration-300"
            >
              <Facebook size={16} />
            </a>
            <a
              href="https://www.tiktok.com/@..dr.dee"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="flex items-center justify-center w-9 h-9 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/60 hover:bg-white/10 transition-all duration-300"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.19 8.19 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.15z" />
              </svg>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
            <p
              className="text-[10px] text-white/40 tracking-wide"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Website by{" "}
              <a
                href="https://gloriouslabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors duration-200"
              >
                Glorious
              </a>
            </p>
            <p
              className="text-[10px] sm:text-xs text-white/75"
              style={{ fontFamily }}
            >
              {t("footer.copyright")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
